import { useCallback, useEffect, useRef, useState } from "react";

import {
  DateRange,
  RangePreset,
  presetForRange,
  rangeForPreset,
} from "@/lib/date-range";
import { useTodayIso } from "./useTenantTimezone";

/**
 * A date range anchored to the **tenant's** today (FEAT-2026-012).
 *
 * The chosen preset is held as state rather than inferred from the range,
 * because that inference is lossy: two presets can describe the same dates.
 * On the 30th of a month "Last 30 days" (today−29 → today) and "This month"
 * (1st → today) are identical, so a range-matching lookup returned whichever
 * came first in the list and the other chip never lit up — it read as broken
 * even though clicking it applied the correct dates. Remembering what the user
 * actually pressed makes the UI honest, and it happens 11 days a year.
 *
 * "Today" can also change after mount — the timezone arrives asynchronously
 * with tenant settings, and a long-lived tab eventually crosses midnight. When
 * the anchor moves, a preset range is rebuilt against the new anchor; a range
 * the user typed by hand ("custom") is left exactly as they set it.
 */
export function useAnchoredDateRange(initial: Exclude<RangePreset, "custom">) {
  const today = useTodayIso();
  const [state, setState] = useState<{ preset: RangePreset; range: DateRange }>(() => ({
    preset: initial,
    range: rangeForPreset(initial, today),
  }));
  const anchorRef = useRef(today);

  useEffect(() => {
    if (anchorRef.current === today) return;
    anchorRef.current = today;
    setState((current) =>
      current.preset === "custom"
        ? current
        : { ...current, range: rangeForPreset(current.preset, today) },
    );
  }, [today]);

  /** Pick a preset chip — the selection is remembered as-is. */
  const selectPreset = useCallback(
    (preset: Exclude<RangePreset, "custom">) => {
      setState({ preset, range: rangeForPreset(preset, today) });
    },
    [today],
  );

  /**
   * Set explicit dates (the From/To inputs). If the result happens to match a
   * preset exactly we adopt that preset so the chip lights up; otherwise the
   * selection becomes "custom".
   */
  const setRange = useCallback(
    (range: DateRange) => {
      setState({ preset: presetForRange(range, today), range });
    },
    [today],
  );

  return { range: state.range, preset: state.preset, selectPreset, setRange, today };
}
